import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiClient } from '../../auth-lib';
import { Button, Card, Input, Modal, Select, Skeleton, useToast } from '../../design-system';

const FREQUENCIES = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'half_yearly', label: 'Half-yearly' },
  { value: 'yearly', label: 'Yearly' },
];
const FREQ_LABEL = Object.fromEntries(FREQUENCIES.map((f) => [f.value, f.label]));
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

const ordinal = (n) => {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

// Plain-English summary of a stored rule, so the schedule can be understood
// without decoding months/weeks/offset arrays.
function describe(rec) {
  if (!rec) return '';
  const every = rec.interval > 1 ? `Every ${rec.interval} ` : 'Every ';
  const unit = { daily: 'day', weekly: 'week', monthly: 'month', quarterly: 'quarter', half_yearly: 'half-year', yearly: 'year' }[rec.frequency];
  const parts = [`${every}${unit}${rec.interval > 1 ? 's' : ''}`];

  if (rec.months?.length && rec.months.length < 12 && rec.frequency !== 'daily' && rec.frequency !== 'weekly') {
    parts.push(`in ${rec.months.map((m) => MONTHS[m - 1]).join(', ')}`);
  }
  if (rec.frequency === 'weekly' && rec.weeks?.length && rec.weeks.length < 5) {
    parts.push(`in week ${rec.weeks.map((w) => (w === 5 ? 'last' : ordinal(w))).join(', ')} of the month`);
  }
  if (rec.frequency === 'yearly' && rec.years?.length) {
    parts.push(`in ${rec.years.join(', ')}`);
  }
  if (rec.dueOnMonthEnd) {
    parts.push(`due the last day${rec.monthOffset ? ` of the month ${rec.monthOffset} on` : ''}`);
  } else if (rec.dueDay) {
    const off = rec.monthOffset ?? 1;
    parts.push(
      off === 0 ? `due the ${ordinal(rec.dueDay)} of the period month`
        : off === 1 ? `due the ${ordinal(rec.dueDay)} of the following month`
          : `due the ${ordinal(rec.dueDay)}, ${off} months after`
    );
  } else {
    parts.push('with no due date');
  }

  if (rec.endType === 'after' && rec.occurrences) parts.push(`· ends after ${rec.occurrences} times`);
  else if (rec.endType === 'on' && rec.endDate) parts.push(`· ends ${fmtDate(rec.endDate)}`);

  return parts.join(' ');
}

// The schedule behind a recurring task: what it does, what's coming, and how to
// change or stop it.
//
// Editing only ever affects FUTURE occurrences — the ones already generated are
// real tasks people may have started, so the server leaves them exactly as they
// are. Stopping is not deleting: everything already created stays.
function TaskRecurrenceCard({ taskId, locked, onChanged }) {
  const toast = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await apiClient.get(`/api/tasks/${taskId}/recurrence`);
      setData(res.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [taskId]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <Card padding="md"><Skeleton.Text lines={3} /></Card>;
  // Not part of any series — nothing to say.
  if (!data || (!data.isTemplate && !data.parent)) return null;

  // A generated occurrence: point at the series rather than pretending it owns
  // a schedule it can't edit.
  if (!data.isTemplate) {
    return (
      <Card padding="md" className="td-recur-card">
        <div className="td-card-head"><span className="td-card-title">Recurring</span></div>
        <div className="td-recur-summary">
          This task was generated for <strong>{data.period || 'a period'}</strong> from a repeating schedule.
        </div>
        {data.parent && (
          <Link to={`/org-admin/tasks/${data.parent.id}`} className="td-add-link">
            Open the series →
          </Link>
        )}
      </Card>
    );
  }

  const rec = data.recurrence;
  const stopped = !rec || rec.isExhausted;

  const startEdit = () => {
    setForm({
      frequency: rec.frequency || 'monthly',
      interval: String(rec.interval || 1),
      months: rec.months || [],
      dueMode: rec.dueOnMonthEnd ? 'month_end' : rec.dueDay ? 'day' : 'none',
      dueDay: rec.dueDay ? String(rec.dueDay) : '',
      monthOffset: String(rec.monthOffset ?? 1),
      endType: rec.endType || 'never',
      endDate: rec.endDate ? new Date(rec.endDate).toISOString().slice(0, 10) : '',
      occurrences: rec.occurrences ? String(rec.occurrences) : '',
    });
    setEditing(true);
  };

  const toggleMonth = (m) =>
    setForm((p) => ({
      ...p,
      months: p.months.includes(m) ? p.months.filter((x) => x !== m) : [...p.months, m].sort((a, b) => a - b),
    }));

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        recurrence: {
          isRecurring: true,
          frequency: form.frequency,
          interval: Number(form.interval) || 1,
          months: ['daily', 'weekly'].includes(form.frequency) ? [] : form.months,
          weeks: rec.weeks || [],
          years: rec.years || [],
          endType: form.endType,
          endDate: form.endType === 'on' ? form.endDate || null : null,
          occurrences: form.endType === 'after' ? Number(form.occurrences) || null : null,
          dueOnMonthEnd: form.dueMode === 'month_end',
          dueDay: form.dueMode === 'day' && form.dueDay ? Number(form.dueDay) : null,
          monthOffset: form.dueMode !== 'none' ? Number(form.monthOffset) || 0 : 1,
        },
      };
      await apiClient.put(`/api/tasks/${taskId}/recurrence`, body);
      setEditing(false);
      await load();
      onChanged?.();
      toast.success('Schedule updated — future occurrences only');
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Failed to update the schedule');
    } finally {
      setSaving(false);
    }
  };

  const stop = async () => {
    if (!window.confirm('Stop this schedule? Tasks already created are kept; no new ones will be generated.')) return;
    try {
      await apiClient.put(`/api/tasks/${taskId}/recurrence`, { stop: true });
      await load();
      onChanged?.();
      toast.success('Schedule stopped');
    } catch (err) {
      toast.danger(err.response?.data?.error || 'Failed to stop the schedule');
    }
  };

  return (
    <Card padding="md" className="td-recur-card">
      <div className="td-card-head">
        <span className="td-card-title">
          Recurring {stopped && <span className="td-count">stopped</span>}
        </span>
        {!locked && !stopped && (
          <span className="td-recur-actions">
            <button type="button" className="td-add-link" onClick={startEdit}>Edit</button>
            <button type="button" className="td-add-link td-add-link-danger" onClick={stop}>Stop</button>
          </span>
        )}
        {!locked && stopped && (
          <button type="button" className="td-add-link" onClick={startEdit}>Restart</button>
        )}
      </div>

      <div className="td-recur-summary">{describe(rec)}</div>
      <div className="td-muted td-recur-count">
        {data.generatedCount} occurrence{data.generatedCount === 1 ? '' : 's'} so far
        {rec?.endType === 'after' && rec.occurrences ? ` of ${rec.occurrences}` : ''}
      </div>

      {stopped ? (
        <div className="td-muted">No further occurrences will be generated.</div>
      ) : (
        <>
          <div className="td-history-subhead">Next up</div>
          {data.upcoming.length === 0 ? (
            <div className="td-muted">Nothing further scheduled.</div>
          ) : (
            <div className="td-recur-list">
              {data.upcoming.map((u) => (
                <div key={u.periodLabel} className="td-recur-row">
                  <span className="td-recur-period">{u.periodLabel}</span>
                  <span className="td-muted">{u.dueDate ? `due ${fmtDate(u.dueDate)}` : 'no due date'}</span>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      <Modal
        isOpen={editing}
        onClose={() => setEditing(false)}
        title="Edit repeating schedule"
        footer={
          <>
            <Button variant="secondary" onClick={() => setEditing(false)} disabled={saving}>Cancel</Button>
            <Button variant="primary" onClick={save} loading={saving}>Save</Button>
          </>
        }
      >
        {form && (
          <div className="td-recur-form">
            <p className="td-muted">
              Changes apply to <strong>future occurrences only</strong>. Tasks already generated keep
              their dates.
            </p>

            <Select
              label="Repeats"
              value={form.frequency}
              onChange={(e) => setForm({ ...form, frequency: e.target.value })}
            >
              {FREQUENCIES.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
            </Select>

            <Input
              label="Every"
              type="number"
              min="1"
              value={form.interval}
              onChange={(e) => setForm({ ...form, interval: e.target.value })}
              helperText={`${FREQ_LABEL[form.frequency]} interval`}
            />

            {!['daily', 'weekly'].includes(form.frequency) && (
              <div className="ds-field">
                <label className="ds-field-label">Months it runs in</label>
                <div className="td-recur-months">
                  {MONTHS.map((m, i) => (
                    <button
                      type="button"
                      key={m}
                      className={`td-recur-month ${form.months.includes(i + 1) ? 'is-on' : ''}`}
                      onClick={() => toggleMonth(i + 1)}
                    >
                      {m}
                    </button>
                  ))}
                </div>
                <span className="ds-field-helper">None selected = every month at this cadence.</span>
              </div>
            )}

            <Select
              label="Due date for each occurrence"
              value={form.dueMode}
              onChange={(e) => setForm({ ...form, dueMode: e.target.value })}
            >
              <option value="none">No due date</option>
              <option value="day">A day of the month</option>
              <option value="month_end">Last day of the month</option>
            </Select>

            {form.dueMode === 'day' && (
              <Input
                label="Day of month"
                type="number"
                min="1"
                max="31"
                value={form.dueDay}
                onChange={(e) => setForm({ ...form, dueDay: e.target.value })}
                helperText="31 lands on the real last day in shorter months."
              />
            )}
            {form.dueMode !== 'none' && (
              <Input
                label="Months after the period"
                type="number"
                min="0"
                value={form.monthOffset}
                onChange={(e) => setForm({ ...form, monthOffset: e.target.value })}
                helperText="1 = the following month (GSTR-3B style)."
              />
            )}

            <Select
              label="Ends"
              value={form.endType}
              onChange={(e) => setForm({ ...form, endType: e.target.value })}
            >
              <option value="never">Never</option>
              <option value="on">On a date</option>
              <option value="after">After N occurrences</option>
            </Select>
            {form.endType === 'on' && (
              <Input label="End date" type="date" value={form.endDate} onChange={(e) => setForm({ ...form, endDate: e.target.value })} />
            )}
            {form.endType === 'after' && (
              <Input
                label="Number of occurrences"
                type="number"
                min="1"
                value={form.occurrences}
                onChange={(e) => setForm({ ...form, occurrences: e.target.value })}
                helperText={`Counts the ${data.generatedCount} already created.`}
              />
            )}
          </div>
        )}
      </Modal>
    </Card>
  );
}

export default TaskRecurrenceCard;
